import { Config } from '../config/Config';
import { getFromStorage } from './Helper';

export const SOCIAL_PROFILE_PREFILL_KEY = 'social_profile_prefill';
/** Set before redirect to complete-profile; cleared when that screen is ready. */
export const SOCIAL_PROFILE_TRANSITION_KEY = 'social_profile_transition';

export type SocialProfilePrefill = {
  email?: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  social_type?: string;
};

function trimStr(value: unknown): string {
  if (value == null) return '';
  return String(value).trim();
}

function parseBio(raw: unknown): Record<string, unknown> {
  if (raw == null) return {};
  if (typeof raw === 'object') return raw as Record<string, unknown>;
  try {
    const parsed = JSON.parse(String(raw));
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function mergedBusinessProfile(profile: any): Record<string, unknown> {
  const bio = parseBio(profile?.bio);
  const bd = profile?.business_details || profile?.businessDetails || {};
  return {
    ...bio,
    ...bd,
    company_name: bd?.company_name ?? bio?.company_name ?? profile?.company_name,
    company_email:
      bd?.company_email ?? bio?.company_email ?? profile?.company_email ?? profile?.email,
  };
}

export function splitSocialDisplayName(fullName: string): { first_name: string; last_name: string } {
  const normalized = String(fullName || '').trim().replace(/\s+/g, ' ');
  if (!normalized) return { first_name: '', last_name: '' };
  const parts = normalized.split(' ');
  return {
    first_name: parts[0] || '',
    last_name: parts.slice(1).join(' ') || '',
  };
}

/** Social sign-in users must finish onboarding before using the app. */
export function needsSocialProfileCompletion(profile: any): boolean {
  if (!profile?.social_id) return false;

  const accountType = String(profile.account_type || '').toLowerCase().trim();
  if (accountType !== 'individual' && accountType !== 'business') {
    return true;
  }

  if (accountType === 'individual') {
    return !trimStr(profile.first_name) || !trimStr(profile.last_name);
  }

  // Business: server stores company on `name` + bio; first/last are cleared intentionally.
  const m = mergedBusinessProfile(profile);
  const companyName = trimStr(m.company_name) || trimStr(profile.name);
  const companyEmail =
    trimStr(m.company_email) || trimStr(m.companyEmail) || trimStr(profile.email);
  return !companyName || !companyEmail;
}

/** Router-relative path (basename applied separately). */
export const COMPLETE_PROFILE_PATH = '/signup/complete-profile';

export function isCompleteProfilePath(pathname: string): boolean {
  return pathname.includes(COMPLETE_PROFILE_PATH);
}

const PROFILE_GATE_EXEMPT_PATHS = [
  COMPLETE_PROFILE_PATH,
  '/login',
  '/signup',
  '/success',
  '/cancel',
  '/failed',
  '/user/privacy-policy',
  '/user/terms-and-conditions',
  '/user/about-us',
  '/user/faq',
];

export function isSocialProfileGateExemptPath(pathname: string): boolean {
  return PROFILE_GATE_EXEMPT_PATHS.some((segment) => pathname.includes(segment));
}

/** Enrich raw API user (e.g. update-profile) with business fields from bio. */
export function enrichProfileUser(user: any): any {
  if (!user || typeof user !== 'object') return user;
  const accountType = String(user.account_type || '').toLowerCase().trim();
  if (accountType !== 'business') return user;

  const m = mergedBusinessProfile(user);
  return {
    ...user,
    account_type: 'business',
    company_name: m.company_name ?? user.company_name,
    company_email: m.company_email ?? user.company_email,
    business_details: {
      ...(user.business_details || {}),
      company_name: m.company_name,
      company_email: m.company_email,
    },
  };
}

/** Router path after profile completion (relative to app basename). */
export function getPostSocialLoginPath(user: any): string {
  if (String(user?.is_category_select ?? '') === '0') {
    return '/category';
  }
  return '/';
}

/** Full URL for hard redirects (e.g. after social login). */
export function getPostSocialLoginRedirect(user: any): string {
  const base = Config.BASE_URL || '';
  const path = getPostSocialLoginPath(user);
  if (!base) return path;
  return `${base.replace(/\/$/, '')}${path}`;
}

/** Full path for hard redirect to complete-profile (includes CRA `homepage` basename). */
export function getCompleteProfileRedirect(): string {
  const base = Config.BASE_URL || '';
  if (!base) return COMPLETE_PROFILE_PATH;
  return `${base.replace(/\/$/, '')}${COMPLETE_PROFILE_PATH}`;
}

export function readStoredUserForProfileGate(): any {
  const fromStorage = getFromStorage({ key: 'user_data' });
  const data = fromStorage?.data ?? fromStorage;
  if (!data || typeof data !== 'object') return null;
  return enrichProfileUser(data);
}

export function readSocialProfilePrefill(): SocialProfilePrefill | null {
  try {
    const raw = sessionStorage.getItem(SOCIAL_PROFILE_PREFILL_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SocialProfilePrefill;
  } catch {
    return null;
  }
}

export function saveSocialProfilePrefill(prefill: SocialProfilePrefill): void {
  try {
    sessionStorage.setItem(SOCIAL_PROFILE_PREFILL_KEY, JSON.stringify(prefill));
  } catch {
    /* ignore quota */
  }
}

export function clearSocialProfilePrefill(): void {
  try {
    sessionStorage.removeItem(SOCIAL_PROFILE_PREFILL_KEY);
  } catch {
    /* ignore */
  }
}

export function markSocialProfileTransition(): void {
  try {
    sessionStorage.setItem(SOCIAL_PROFILE_TRANSITION_KEY, '1');
  } catch {
    /* ignore quota */
  }
}

export function isSocialProfileTransitionPending(): boolean {
  try {
    return sessionStorage.getItem(SOCIAL_PROFILE_TRANSITION_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearSocialProfileTransition(): void {
  try {
    sessionStorage.removeItem(SOCIAL_PROFILE_TRANSITION_KEY);
  } catch {
    /* ignore */
  }
}
